import React, { useState, useEffect } from 'react';
import { timeTrackingAPI } from '../../services/api';
import './TimeTracking.css';
import { formatGermanTime } from '../../utils/timezone';
import { 
  Clock, 
  Smartphone, 
  Monitor, 
  Edit3, 
  AlertTriangle, 
  CheckCircle, 
  XCircle, 
  AlertCircle 
} from 'lucide-react';

interface WorkLog {
  id: number;
  startTime: string;
  endTime: string | null;
  description: string | null;
  logMethod: string;
  logMethod2: string;
  totalMinutes: number | null;
  breakMinutes?: number | null;
  user: {
    firstName: string;
    lastName: string;
  };
}

interface TimeTrackingProps {
  user: {
    webTimeTrackingEnabled?: boolean;
  };
}

export const TimeTracking: React.FC<TimeTrackingProps> = ({ }) => {
  const [currentWorkLog, setCurrentWorkLog] = useState<WorkLog | null>(null);
  const [isWorking, setIsWorking] = useState(false);
  const [elapsedTime, setElapsedTime] = useState('00:00:00');
  const [description, setDescription] = useState('');
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState({ type: '', text: '' });
  const [todayWorkLogs, setTodayWorkLogs] = useState<WorkLog[]>([]);
  const [todayTotal, setTodayTotal] = useState(0);
  const [maxWorkHours, setMaxWorkHours] = useState(12);
  const [showMaxTimeWarning, setShowMaxTimeWarning] = useState(false);
  const [webTimeTrackingEnabled, setWebTimeTrackingEnabled] = useState(true);

  useEffect(() => {
    loadCurrentStatus();
    loadTodayWorkLogs();
  }, []);

  useEffect(() => {
    let interval: NodeJS.Timeout;
    
    if (isWorking && currentWorkLog) {
      interval = setInterval(() => {
        // Verbesserte Zeit-Berechnung mit UTC-Handling
        const startTime = new Date(currentWorkLog.startTime);
        const now = new Date();
        
        // Sicherstellen, dass beide Zeiten korrekt sind
        if (isNaN(startTime.getTime())) {
          console.error('Invalid startTime:', currentWorkLog.startTime);
          setElapsedTime('--:--:--');
          return;
        }
        
        const diff = now.getTime() - startTime.getTime();
        
        // Negative Zeiten abfangen (passiert bei Zeitzonenunterschieden)
        if (diff < 0) {
          console.warn('Negative time difference detected, adjusting...');
          setElapsedTime('00:00:00');
          return;
        }
        
        const totalMinutes = Math.floor(diff / (1000 * 60));
        const hours = Math.floor(totalMinutes / 60);
        const minutes = totalMinutes % 60;
        const seconds = Math.floor((diff % (1000 * 60)) / 1000);
        
        // Sicherstellen, dass alle Werte gültig sind
        const safeHours = Math.max(0, hours);
        const safeMinutes = Math.max(0, minutes);
        const safeSeconds = Math.max(0, seconds);
        
        setElapsedTime(
          `${safeHours.toString().padStart(2, '0')}:${safeMinutes.toString().padStart(2, '0')}:${safeSeconds.toString().padStart(2, '0')}`
        );

        const hoursWorked = totalMinutes / 60;
        if (hoursWorked >= maxWorkHours - 0.5 && hoursWorked < maxWorkHours) {
          setShowMaxTimeWarning(true);
        } else if (hoursWorked >= maxWorkHours) {
          loadCurrentStatus();
        }
      }, 1000);
    } else {
      setElapsedTime('00:00:00');
      setShowMaxTimeWarning(false);
    }
    
    return () => {
      if (interval) clearInterval(interval);
    };
  }, [isWorking, currentWorkLog, maxWorkHours]);

  const loadCurrentStatus = async () => {
    try {
      const response = await timeTrackingAPI.getCurrentStatus();
      console.log('Current status response:', response.data);
    
      const { 
        isWorking, 
        activeWorkLog, 
        wasAutoClockOut, 
        maxWorkHours: userMaxHours,
        webTimeTrackingEnabled: webTracking
      } = response.data;

      if (webTracking !== undefined) {
        setWebTimeTrackingEnabled(webTracking);
      }

      if (userMaxHours) {
        setMaxWorkHours(userMaxHours);
      }

      if (wasAutoClockOut) {
        setMessage({
          type: 'warning',
          text: `Sie wurden automatisch nach ${userMaxHours || maxWorkHours} Stunden ausgestempelt.`
        });
        loadTodayWorkLogs();
      }

      if (isWorking && activeWorkLog) {
        setCurrentWorkLog(activeWorkLog);
        setIsWorking(true);
        console.log('✅ User is working, active log:', activeWorkLog.id);
      } else {
        setCurrentWorkLog(null);
        setIsWorking(false);
        setElapsedTime('00:00:00');
        setShowMaxTimeWarning(false);
        console.log('❌ User is not working');
      }
    } catch (error) {
      console.error('Error loading current status:', error);
      if ((error as any)?.response?.status === 403) {
        setWebTimeTrackingEnabled(false);
        setMessage({
          type: 'error',
          text: (error as any)?.response?.data?.error || 'Website-Zeiterfassung nicht verfügbar'
        });
      }
    }
  };

  const loadTodayWorkLogs = async () => {
    try {
      const response = await timeTrackingAPI.getTodayWorkLogs();
      setTodayWorkLogs(response.data.workLogs);
      setTodayTotal(response.data.totalMinutes);
    } catch (error) {
      console.error('Error loading today work logs:', error);
    }
  };

  const handleClockIn = async () => {
    setLoading(true);
    setMessage({ type: '', text: '' });

    try {
      const response = await timeTrackingAPI.clockIn({ description });
      setCurrentWorkLog(response.data.workLog);
      setIsWorking(true);
      setDescription('');
      
      if (response.data.maxWorkHours) {
        setMaxWorkHours(response.data.maxWorkHours);
      }

      setMessage({ 
        type: 'success', 
        text: `Erfolgreich eingestempelt um ${new Date().toLocaleTimeString('de-DE')}. Max. Arbeitszeit: ${response.data.maxWorkHours || maxWorkHours}h` 
      });
      loadTodayWorkLogs();
    } catch (error: any) {
      setMessage({
        type: 'error',
        text: error.response?.data?.error || 'Fehler beim Einstempeln'
      });
    } finally {
      setLoading(false);
    }
  };

  const handleClockOut = async () => {
    if (!currentWorkLog) return;

    setLoading(true);
    setMessage({ type: '', text: '' });

    try {
      const response = await timeTrackingAPI.clockOut(currentWorkLog.id, { description });
      setCurrentWorkLog(null);
      setIsWorking(false);
      setElapsedTime('00:00:00');
      setDescription('');
      
      const duration = response.data.duration;
      let messageText = `Erfolgreich ausgestempelt. Arbeitszeit: ${duration.hours}h ${duration.minutes}m`;

      if (duration.breakMinutes && duration.breakMinutes > 0) {
        const grossHours = Math.floor(duration.grossMinutes / 60);
        const grossMins = duration.grossMinutes % 60;
        messageText += ` (Brutto: ${grossHours}h ${grossMins}m, Pause: -${duration.breakMinutes}m)`;
      }
      
      setMessage({ type: 'success', text: messageText });
      loadTodayWorkLogs();
    } catch (error: any) {
      setMessage({
        type: 'error',
        text: error.response?.data?.error || 'Fehler beim Ausstempeln'
      });
    } finally {
      setLoading(false);
    }
  };

  const formatDuration = (minutes: number) => {
    const hours = Math.floor(minutes / 60);
    const mins = minutes % 60;
    return `${hours}h ${mins}m`;
  };

  const formatTime = (dateString: string) => {
    return formatGermanTime(dateString);
  };

  const getTodayTotalFormatted = () => {
    if (!todayTotal || todayTotal === 0) return '0:00';
    const hours = Math.floor(todayTotal / 60);
    const minutes = todayTotal % 60;
    return `${hours}:${minutes.toString().padStart(2, '0')}`;
  };

  if (!webTimeTrackingEnabled) {
    return (
      <div className="time-tracking">
        <h2>
          <Clock size={24} style={{ marginRight: '8px', verticalAlign: 'middle' }} />
          Ein/Ausstempeln
        </h2>

        <div className="restriction-message">
          <div className="message error">
            <XCircle size={20} style={{ marginRight: '8px', verticalAlign: 'middle' }} />
            Website-Zeiterfassung ist für Ihren Account deaktiviert.
            <br />
            <Smartphone size={20} style={{ marginRight: '8px', verticalAlign: 'middle' }} />
            Bitte verwenden Sie Ihre NFC-Karte für die Zeiterfassung.
          </div>
        </div>

        <div className="nfc-info-card">
          <h3>
            <Smartphone size={20} style={{ marginRight: '8px', verticalAlign: 'middle' }} />
            NFC-Zeiterfassung
          </h3>
          <p>
            Halten Sie Ihre NFC-Karte an das Lesegerät, um sich ein- oder auszustempeln.
            <br />
            Ihre Arbeitszeiten werden automatisch erfasst und sind in Ihren Berichten einsehbar.
          </p>
          
          <div className="nfc-instructions">
            <div className="instruction-item">
              <span className="instruction-icon">
                <CheckCircle size={20} style={{ color: '#10b981' }} />
              </span>
              <span>Grünes Licht = Erfolgreich eingestempelt</span>
            </div>
            <div className="instruction-item">
              <span className="instruction-icon">
                <XCircle size={20} style={{ color: '#dc3545' }} />
              </span>
              <span>Rotes Licht = Erfolgreich ausgestempelt</span>
            </div>
            <div className="instruction-item">
              <span className="instruction-icon">
                <AlertCircle size={20} style={{ color: '#f59e0b' }} />
              </span>
              <span>Gelbes Licht = Fehler, bitte erneut versuchen</span>
            </div>
          </div>
        </div>

        {/* Today Summary - still show this even if web tracking is disabled */}
        <div className="today-summary">
          <h3>Heute ({new Date().toLocaleDateString('de-DE')})</h3>
          
          <div className="summary-stats">
            <div className="stat-item">
              <span className="stat-label">Netto-Arbeitszeit:</span>
              <span className="stat-value">{getTodayTotalFormatted()}</span>
            </div>
            <div className="stat-item">
              <span className="stat-label">Arbeitssitzungen:</span>
              <span className="stat-value">{todayWorkLogs.length}</span>
            </div>
          </div>

          {todayWorkLogs.length > 0 && (
            <div className="work-logs-table">
              <table>
                <thead>
                  <tr>
                    <th>Start</th>
                    <th>Ende</th>
                    <th>Dauer</th>
                    <th>Beschreibung</th>
                    <th>Methode</th>
                  </tr>
                </thead>
                <tbody>
                  {todayWorkLogs.map((log) => (
                    <tr key={log.id} className={!log.endTime ? 'active-log' : ''}>
                      <td>{formatTime(log.startTime)}</td>
                      <td>{log.endTime ? formatTime(log.endTime) : 'Läuft...'}</td>
                      <td>
                        {log.endTime && log.totalMinutes ? (
                          <div className="duration-info">
                            <span className="main-duration">{formatDuration(log.totalMinutes)}</span>
                            {log.breakMinutes && log.breakMinutes > 0 && (
                              <small className="break-info">
                                (Brutto: {formatDuration(log.totalMinutes + log.breakMinutes)}, 
                                Pause: -{formatDuration(log.breakMinutes)})
                              </small>
                            )}
                          </div>
                        ) : log.endTime ? (
                          (() => {
                            const start = new Date(log.startTime).getTime();
                            const end = new Date(log.endTime).getTime();
                            const minutes = Math.floor((end - start) / (1000 * 60));
                            return formatDuration(minutes);
                          })()
                        ) : (
                          'Läuft...'
                        )}
                      </td>
                      <td className="description-cell">
                        {log.description || '-'}
                      </td>
                      <td>
                        <div className="method-badges">
                          <span className={`method-badge ${log.logMethod.toLowerCase()}`}>
                            {log.logMethod === 'WEBSITE' ? (
                              <Monitor size={16} style={{ marginRight: '4px' }} />
                            ) : log.logMethod === 'NFC' ? (
                              <Smartphone size={16} style={{ marginRight: '4px' }} />
                            ) : (
                              <Edit3 size={16} style={{ marginRight: '4px' }} />
                            )} 
                            {log.logMethod}
                          </span>
                          {log.endTime && log.logMethod2 && (
                            <>
                              <span className="method-arrow">→</span>
                              <span className={`method-badge ${log.logMethod2.toLowerCase()}`}>
                                {log.logMethod2 === 'WEBSITE' ? (
                                  <Monitor size={16} style={{ marginRight: '4px' }} />
                                ) : log.logMethod2 === 'NFC' ? (
                                  <Smartphone size={16} style={{ marginRight: '4px' }} />
                                ) : (
                                  <Edit3 size={16} style={{ marginRight: '4px' }} />
                                )} 
                                {log.logMethod2}
                              </span>
                            </>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>
    );
  }

  return (
    <div className="time-tracking">
      <h2>
        <Clock size={24} style={{ marginRight: '8px', verticalAlign: 'middle' }} />
        Ein/Ausstempeln
      </h2>

      {message.text && (
        <div className={`message ${message.type}`}>
          {message.text}
          <button 
            className="message-close" 
            onClick={() => setMessage({ type: '', text: '' })}
          >
            ×
          </button>
        </div>
      )}

      {showMaxTimeWarning && (
        <div className="message warning">
          <AlertTriangle size={20} style={{ marginRight: '8px', verticalAlign: 'middle' }} />
          Sie nähern sich der maximalen Arbeitszeit von {maxWorkHours} Stunden. 
          Sie werden automatisch ausgestempelt, wenn diese erreicht wird.
          <button 
            className="message-close" 
            onClick={() => setShowMaxTimeWarning(false)}
          >
            ×
          </button>
        </div>
      )}

      {/* Status Card */}
      <div className="status-card">
        <div className="status-header">
          <div className={`tt-status-indicator ${isWorking ? 'working' : 'not-working'}`}>
            <span className="tt-status-dot"></span>
            <span className="tt-status-text">
              {isWorking ? 'Eingestempelt' : 'Ausgestempelt'}
            </span>
          </div>
        </div>

        {isWorking ? (
          <div className="working-status">
            <div className="timer">
              <span className="timer-label">Arbeitszeit heute:</span>
              <span className={`timer-display ${showMaxTimeWarning ? 'warning' : ''}`}>
                {elapsedTime}
              </span>
              <small className="max-time-info">
                (Der Timer starte mit der Serverzeit und kann bei Zeitabweichungen variieren.)
              </small>
              <small className="max-time-info">
                Max. Arbeitszeit: {maxWorkHours}h
              </small>
            </div>
            
            <div className="clock-out-section">
              <div className="form-group">
                <label>Beschreibung (optional):</label>
                <input
                  type="text"
                  value={description}
                  onChange={(e) => setDescription(e.target.value)}
                  placeholder="Was haben Sie heute gemacht?"
                  className="description-input"
                />
              </div>
              
              <button 
                className="btn danger clock-btn" 
                onClick={handleClockOut}
                disabled={loading}
              >
                {loading ? 'Ausstempeln...' : 'Ausstempeln'}
              </button>
            </div>
          </div>
        ) : (
          <div className="not-working-status">
            <div className="clock-in-section">
              <div className="form-group">
                <label>Beschreibung (optional):</label>
                <input
                  type="text"
                  value={description}
                  onChange={(e) => setDescription(e.target.value)}
                  placeholder="Woran werden Sie heute arbeiten?"
                  className="description-input"
                />
              </div>
              
              <button 
                className="btn success clock-btn" 
                onClick={handleClockIn}
                disabled={loading}
              >
                {loading ? 'Einstempeln...' : 'Einstempeln'}
              </button>
            </div>
          </div>
        )}
      </div>

      {/* Today Summary */}
      <div className="today-summary">
        <h3>Heute ({new Date().toLocaleDateString('de-DE')})</h3>
        
        <div className="summary-stats">
          <div className="stat-item">
            <span className="stat-label">Netto-Arbeitszeit:</span>
            <span className="stat-value">{getTodayTotalFormatted()}</span>
          </div>
          <div className="stat-item">
            <span className="stat-label">Arbeitssitzungen:</span>
            <span className="stat-value">{todayWorkLogs.length}</span>
          </div>
        </div>

        {todayWorkLogs.length > 0 && (
          <div className="work-logs-table">
            <table>
              <thead>
                <tr>
                  <th>Start</th>
                  <th>Ende</th>
                  <th>Dauer</th>
                  <th>Beschreibung</th>
                  <th>Methode</th>
                </tr>
              </thead>
              <tbody>
                {todayWorkLogs.map((log) => (
                  <tr key={log.id} className={!log.endTime ? 'active-log' : ''}>
                    <td>{formatTime(log.startTime)}</td>
                    <td>{log.endTime ? formatTime(log.endTime) : 'Läuft...'}</td>
                    <td>
                      {log.endTime && log.totalMinutes ? (
                        <div className="duration-info">
                          <span className="main-duration">{formatDuration(log.totalMinutes)}</span>
                          {log.breakMinutes && log.breakMinutes > 0 && (
                            <small className="break-info">
                              (Brutto: {formatDuration(log.totalMinutes + log.breakMinutes)}, 
                              Pause: -{formatDuration(log.breakMinutes)})
                            </small>
                          )}
                        </div>
                      ) : log.endTime ? (
                        (() => {
                          const start = new Date(log.startTime).getTime();
                          const end = new Date(log.endTime).getTime();
                          const minutes = Math.floor((end - start) / (1000 * 60));
                          return formatDuration(minutes);
                        })()
                      ) : (
                        'Läuft...'
                      )}
                    </td>
                    <td className="description-cell">
                      {log.description || '-'}
                    </td>
                    <td>
                      <div className="method-badges">
                        <span className={`method-badge ${log.logMethod.toLowerCase()}`}>
                          {log.logMethod === 'WEBSITE' ? (
                            <Monitor size={16} style={{ marginRight: '4px' }} />
                          ) : log.logMethod === 'NFC' ? (
                            <Smartphone size={16} style={{ marginRight: '4px' }} />
                          ) : (
                            <Edit3 size={16} style={{ marginRight: '4px' }} />
                          )} 
                          {log.logMethod}
                        </span>
                        {log.endTime && log.logMethod2 && (
                          <>
                            <span className="method-arrow">→</span>
                            <span className={`method-badge ${log.logMethod2.toLowerCase()}`}>
                              {log.logMethod2 === 'WEBSITE' ? (
                                <Monitor size={16} style={{ marginRight: '4px' }} />
                              ) : log.logMethod2 === 'NFC' ? (
                                <Smartphone size={16} style={{ marginRight: '4px' }} />
                              ) : (
                                <Edit3 size={16} style={{ marginRight: '4px' }} />
                              )} 
                              {log.logMethod2}
                            </span>
                          </>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
};